<script lang="ts">
  /**
   * Screens, switched by a plain `$state` string: no router. The menu (the
   * shell's `AppFrame`: Home, Daily, Together and You) is the base; the solo
   * game, the results screen, the Together tab's two sub-screens (start a
   * game, join a game), the room, a round on one device
   * (`docs/decisions/0059`) and the admin panel are full-screen takeovers on
   * top of it.
   *
   * ## The frame (`docs/decisions/0054`)
   *
   * The shell's `AppFrame` draws the frame: on a phone a top bar (on Home
   * Interlock's mark and name as the page's heading, elsewhere the small
   * mark and the tab's name; the round profile button), the tab and the tab
   * bar; from 1024 px a sidebar with the mark and the name, the tabs and the
   * profile button with the player's name (`game`, shell 1.4.0,
   * `docs/decisions/0057`). The app is a column
   * (`app.css`): a phone's width, on the backdrop with the column's edge from
   * 481 px, and the wide column from 1024 px. Everything about the app and
   * the account (settings, how to play, installing, signing in, the admin
   * panel) is behind the profile button, in the profile sheet; this file
   * owns those pages, because they are reached from more than one screen.
   *
   * ## Back (`docs/decisions/0047`, `0054`)
   *
   * One history stack, the shell's. Every overlay goes through its
   * `pushOverlay`, directly or through `Sheet` and `FullPage`, so the Back
   * gesture closes the top one instead of leaving the app or the installed
   * PWA.
   *
   * - **The tabs** follow the owner's rule, kept by the shell's `TabHistory`:
   *   leaving Home arms one entry, Back on any other tab returns Home and
   *   never closes the app, and switching between the others re-targets that
   *   entry. The menu unmounts under a takeover, so the history is held here,
   *   at app level, and handed to `AppFrame` (`tabHistory`): its entry stays
   *   under the takeover, and Back from a room lands on the tab it was
   *   entered from.
   * - **A takeover** pushes **one** entry (`enterTakeover`), which stays for
   *   as long as any takeover is showing (a run and the results screen it
   *   ends on share it, and so do the start screen and the room it creates)
   *   and is given back when the app returns to the tabs, however it does.
   *   It is pushed in the same call that sets `screen`, not from an effect:
   *   the game and duel screens stack a back guard on top of it when they
   *   mount, and a child's effects run before its parent's.
   *
   * ## Offline
   *
   * BASELINE 5, "every solo mode is fully playable offline", is why the
   * room is a branch off the menu rather than a wrapper around it. Nothing on
   * the solo path touches the network, and a failure to reach the server can
   * only ever strand you on a Together screen, which is still just a screen.
   * Every probe below (account, daily, records) is fire-and-forget for the
   * same reason: nothing waits for one, and each failure settles its store on
   * "unavailable" and changes nothing else.
   */
  import { AppFrame, Icon, InstallSheet, TabHistory, pushOverlay } from '@artibens/app-shell';
  import type { ShellTab } from '@artibens/app-shell';
  import { buildMode, encodeModeConfig } from '@interlock/modes';
  import type { ModeConfig, ModeOutcome, PersonalBestScore } from '@interlock/modes';
  import { boardTheme } from '@interlock/themes';
  import type { Room } from 'colyseus.js';

  import { IOS_INSTALL_INTRO, IOS_INSTALL_SHEET_TITLE, IOS_INSTALL_STEPS } from './copy/install.js';
  import InterlockMark from './glyphs/InterlockMark.svelte';
  import { joinRoom } from './net/connect.js';
  import { isJoinPath, parseJoinLocation } from './net/share.js';
  import AccountMenu from './nav/AccountMenu.svelte';
  import AccountPage from './nav/AccountPage.svelte';
  import ChangePasswordPage from './nav/ChangePasswordPage.svelte';
  import ConfettiBurst from './nav/ConfettiBurst.svelte';
  import HowToPlayPage from './nav/HowToPlayPage.svelte';
  import LocalSetupPicker from './nav/LocalSetupPicker.svelte';
  import MyScoresPage from './nav/MyScoresPage.svelte';
  import NewGamePicker from './nav/NewGamePicker.svelte';
  import Settings from './nav/Settings.svelte';
  import { account, refresh as refreshAccount, watchSettings } from './state/account.svelte.js';
  import { celebration } from './state/celebration.svelte.js';
  import { watchDailyClock } from './state/daily-clock.svelte.js';
  import { loadDailyPlayedDate, saveDailyPlayedDate } from './state/daily-played-io.js';
  import { dailyIsWaiting } from './state/daily-view.js';
  import { daily, refreshDaily } from './state/daily.svelte.js';
  import { install, promptInstall, watchInstall } from './state/install.svelte.js';
  import type { PersonalBestRecord } from './state/personal-bests-io.js';
  import { refreshRecords } from './state/records.svelte.js';
  import { settings } from './state/settings.svelte.js';
  import { checkForUpdate, updates } from './pwa/register-sw.js';
  import { applyThemeColorMeta } from './pwa/theme-color.js';
  import { INTERLOCK_SIGNATURE } from './theme/accent.js';
  import { showAppearance } from './theme/appearance.js';
  import { rememberAccent, showAccent } from './theme/show-accent.js';
  import AdminScreen from './screens/AdminScreen.svelte';
  import DailyTab from './screens/DailyTab.svelte';
  import DuelScreen from './screens/DuelScreen.svelte';
  import GameScreen from './screens/GameScreen.svelte';
  import HomeTab from './screens/HomeTab.svelte';
  import JoinScreen from './screens/JoinScreen.svelte';
  import LocalRoundScreen from './screens/LocalRoundScreen.svelte';
  import ResultsScreen from './screens/ResultsScreen.svelte';
  import StartScreen from './screens/StartScreen.svelte';
  import TogetherTab from './screens/TogetherTab.svelte';
  import YouTab from './screens/YouTab.svelte';

  type TabId = 'home' | 'daily' | 'together' | 'you';
  type Takeover = 'game' | 'results' | 'start' | 'join' | 'duel' | 'admin' | 'local';
  type Screen = 'tabs' | Takeover;

  /** The admin panel's own hash route. */
  const ADMIN_HASH = '#/admin';

  // The house theme's appearance: Light (the default, the house's since shell
  // 1.4.0) or Dark, from Settings, stamped on the document root by the shell's helper, which also
  // points `<meta name="theme-color">` (Android's status bar, Safari's
  // toolbar) at the page's ground, and remembered for the boot script
  // (`theme/appearance.ts`). A solo run wears its board's ground there
  // instead (the board look follows the appearance, `docs/decisions/0056`),
  // and follows an appearance changed from the Game menu; leaving the run
  // re-runs this and hands the colour back to the page.
  $effect(() => {
    showAppearance(settings.appearance);
    if (onBoard) applyThemeColorMeta(boardTheme(settings.appearance).tokens.background);
  });

  // The app's accent (`theme/accent.ts`, `docs/decisions/0056`): the colour
  // the player chose, resolved by the shell's engine for the base on show and
  // set inline on the root, again whenever the colour, the appearance or a
  // synced blob changes it; and cached for both bases, so the next load's
  // boot script (the shell's `accent-boot.js`) paints it before the first frame.
  // The accent is the app's, not the board's: the board look has fixed
  // colours of its own (`@interlock/themes`), dark or light with the appearance.
  $effect(() => {
    showAccent(settings.accent, settings.appearance);
    rememberAccent(settings.accent);
  });

  // The account probe, and the settings sync it turns on.
  $effect(() => {
    void refreshAccount();
    return watchSettings();
  });

  $effect(() => {
    void refreshDaily();
  });

  $effect(() => {
    void refreshRecords();
  });

  // One install-prompt watcher for the app's whole life; Home, the profile
  // sheet and Settings all read its store (`state/install.svelte.ts`).
  $effect(() => watchInstall());

  // The clock the daily's countdown is read off, on Home and on the Daily tab.
  $effect(() => watchDailyClock());

  /**
   * The whole router: two routes, read at load.
   *
   * A join link (`/join/PQ4T`, or the older `#/join/PQ4T`; `net/share.ts`)
   * opens the join screen with the code filled in and already looked up.
   * `#/admin` opens the admin panel.
   *
   * Both are **spent on arrival**: the address is put back to `/` with
   * `history.replaceState` before anything is pushed, so every entry the
   * shell's stack adds afterwards carries the plain URL. (`pushOverlay`
   * pushes with the URL as it is, and a Back between two entries whose
   * fragments differ would fire `hashchange` and reopen the panel that was
   * just left.) So a route is a way in, not a place: a bookmark still lands
   * where it points, and a reload from inside lands on the tabs.
   */
  const invitedCode = parseJoinLocation(window.location);
  const startsOnAdmin = window.location.hash === ADMIN_HASH;
  const onJoinPath = isJoinPath(window.location.pathname);
  if (invitedCode !== null || startsOnAdmin || onJoinPath) clearRoute();

  /**
   * `replaceState`, never an assignment to `location`: that fires `popstate`,
   * which the shell reads as Back (`packages/shell/INTEGRATION.md` 6.2,
   * rule 6). A join path is replaced with `/`; any other path is kept.
   */
  function clearRoute(): void {
    const path = isJoinPath(window.location.pathname) ? '/' : window.location.pathname;
    history.replaceState(history.state, '', path + window.location.search);
  }

  let screen: Screen = $state<Screen>(
    startsOnAdmin ? 'admin' : invitedCode !== null ? 'join' : 'tabs',
  );
  /** True while a turn of a round on one device has its board on screen. */
  let localPlaying = $state(false);
  /** A board is on screen: a solo run, or a turn on one device. */
  const onBoard = $derived(screen === 'game' || (screen === 'local' && localPlaying));
  /** The tab a load lands on: Together under a join link, Home otherwise. */
  const startTab: TabId = invitedCode !== null ? 'together' : 'home';
  let activeTab: TabId = $state(startTab);

  /**
   * The Back rule of the tabs, held here so that its entry outlives the menu
   * under a takeover (`TabHistory`'s "Who holds it"). Back on a tab that is
   * not Home lands here: show Home, and the frame follows.
   */
  const tabHistory = new TabHistory({
    home: 'home',
    onreturn: () => {
      activeTab = 'home';
    },
  });

  /* --- takeovers ------------------------------------------------------- */

  /** Gives the takeover's history entry back; `null` while the tabs are showing. */
  let releaseTakeover: (() => void) | null = null;

  /**
   * Shows a full-screen takeover. The first one pushes the entry; moving
   * between two (a run to its results, the start screen to its room) keeps
   * it.
   */
  function enterTakeover(next: Takeover): void {
    releaseTakeover ??= pushOverlay(onTakeoverBack);
    screen = next;
  }

  /** Back popped the takeover's entry: close it, and nothing else (INTEGRATION.md 6.2, rule 3). */
  function onTakeoverBack(): void {
    releaseTakeover = null;
    showTabs();
  }

  /** The takeover closes through its own UI: show the tabs, and give the entry back. */
  function leaveTakeover(): void {
    const release = releaseTakeover;
    releaseTakeover = null;
    showTabs();
    release?.();
  }

  /** Back to the tabs, on whichever tab was underneath, with nothing of the takeover left behind. */
  function showTabs(): void {
    // An invitation is spent once it has been offered. Leaving a room or the
    // join screen must not land on a screen that looks the same room up again.
    pendingJoinCode = '';
    joinError = '';
    outcome = null;
    room = null;
    localRound = null;
    localPlaying = false;
    screen = 'tabs';
  }

  // A route at load. The tab a join link belongs to is armed first, as if the
  // player had walked there (Back from the join screen lands on Together,
  // and the next Back on Home); then the screen's own entry, before anything
  // can mount on top of it.
  if (startsOnAdmin || invitedCode !== null) {
    tabHistory.show(startTab);
    releaseTakeover = pushOverlay(onTakeoverBack);
  }

  /**
   * The admin hash has to be *listened* to as well as read: following a
   * `#/admin` link from inside the running app changes only the fragment,
   * which does not reload the page.
   *
   * A fragment navigation also fires `popstate`, which the shell's stack
   * reads as Back and answers by closing its top overlay. That cannot be
   * prevented from here, so this handler is written to be safe after it:
   *
   * - it clears the hash with `replaceState`, so the entry the browser just
   *   added is an ordinary one and leaving the panel later crosses no
   *   fragment change;
   * - it opens the panel **only from the tabs**. During a run or a match the
   *   `popstate` has already popped the back guard, so what the player sees
   *   is the "quit?" question, and the hash is dropped rather than allowed to
   *   throw the run away.
   *
   * The effect body reads no reactive state at set-up, so it runs once and
   * the listener lives as long as the app does. `replaceState` fires neither
   * `hashchange` nor `popstate`, so clearing the hash cannot loop back in.
   */
  $effect(() => {
    const onHashChange = (): void => {
      if (window.location.hash !== ADMIN_HASH) return;
      clearRoute();
      if (screen === 'tabs') enterTakeover('admin');
    };
    window.addEventListener('hashchange', onHashChange);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
    };
  });

  /**
   * A new build that finished installing is applied on the menu and nowhere
   * else: a reload there costs nothing, and anywhere else it would throw
   * away a run, a result or a duel. `pwa/update.ts` holds a waiting build
   * until this says the coast is clear.
   */
  $effect(() => {
    updates.setSafeToReload(screen === 'tabs');
  });

  let pendingJoinCode = $state(invitedCode ?? '');
  let modeConfig: ModeConfig | null = $state(null);
  let outcome: ModeOutcome | null = $state(null);
  let bestResult: { best: PersonalBestRecord; isNewBest: boolean } | null = $state(null);
  /** The finished run's own figure, for the results screen's big number. */
  let runScore: PersonalBestScore | null = $state(null);
  /**
   * The server-assigned seed for the daily challenge, or `null` for an
   * ordinary solo run. Doubles as "was the run now on the results screen a
   * daily attempt": `ResultsScreen` reads `dailySeed !== null` rather than a
   * second flag that could drift from this one.
   */
  let dailySeed: number | null = $state(null);
  let room: Room | null = $state(null);
  let duelRole: 'player' | 'spectator' = $state('player');
  /** Kept so a rejoin can present the same name the first join used. */
  let duelName = $state('');
  let joinError = $state('');
  /** Which of the Together tab's three doors the start screen was opened by. */
  let startPreset: 'ai' | 'duel' | 'coop' = $state('duel');

  function startMode(config: ModeConfig): void {
    modeConfig = config;
    dailySeed = null;
    outcome = null;
    bestResult = null;
    runScore = null;
    enterTakeover('game');
  }

  /**
   * The daily: a fixed Sprint at the day's goal, on the seed the server
   * assigned: never generated here, and never `Date.now()`, which is the
   * whole point of a *shared* daily. With no `daily.info` (the probe has not
   * answered, or the server has none) this does nothing, rather than start a
   * run nobody could post a result for.
   */
  function startDaily(): void {
    if (daily.info === null) return;
    modeConfig = {
      modeId: 'sprint',
      hardness: 'normal',
      goal: { kind: 'lines', lines: daily.info.goalLines },
    };
    dailySeed = daily.info.seed;
    outcome = null;
    bestResult = null;
    runScore = null;
    enterTakeover('game');
  }

  /**
   * The day whose daily was last finished on this device, so the Daily tab's
   * dot goes out for a guest too (`state/daily-played-io.ts`).
   */
  let dailyPlayedDate = $state(loadDailyPlayedDate());

  function finishRun(
    result: ModeOutcome,
    best: { best: PersonalBestRecord; isNewBest: boolean } | null,
    score: PersonalBestScore | null,
  ): void {
    outcome = result;
    bestResult = best;
    runScore = score;
    // A daily that was genuinely finished (a top-out is not) has been played,
    // whether or not there is an account to post it to.
    if (dailySeed !== null && result.result !== 'lose' && daily.info !== null) {
      dailyPlayedDate = daily.info.date;
      saveDailyPlayedDate(daily.info.date);
    }
    enterTakeover('results');
  }

  function rematch(): void {
    outcome = null;
    enterTakeover('game');
  }

  function toHomeTab(): void {
    activeTab = 'home';
    leaveTakeover();
  }

  function toTogetherTab(): void {
    activeTab = 'together';
    leaveTakeover();
  }

  function openStart(preset: 'ai' | 'duel' | 'coop'): void {
    startPreset = preset;
    joinError = '';
    enterTakeover('start');
  }

  /* --- on this device (`docs/decisions/0059`) ---------------------------- */

  let localSetupOpen = $state(false);
  /** The round being played on one device: who, in turn order, and the mode. */
  let localRound: { players: readonly string[]; config: ModeConfig } | null = $state(null);
  /** Counts up per start, so a new start from the setup is a new round screen. */
  let localRoundKey = $state(0);

  function startLocalRound(players: readonly string[], config: ModeConfig): void {
    localRound = { players, config };
    localRoundKey += 1;
    localPlaying = false;
    enterTakeover('local');
  }

  /** "End the round": back to the Together tab with the setup open, the names as they were. */
  function backToLocalSetup(): void {
    activeTab = 'together';
    leaveTakeover();
    localSetupOpen = true;
  }

  function openJoin(): void {
    joinError = '';
    enterTakeover('join');
  }

  /**
   * Enters a room, from the start screen (one it just created) or the join
   * screen. Settles when the join has been tried: on success the room takes
   * the screen over, under the entry the sub-screen already holds; on a
   * refusal the sub-screen stays and says why.
   */
  async function enterRoom(request: {
    roomId: string;
    code: string;
    name: string;
    role: 'player' | 'spectator';
  }): Promise<void> {
    joinError = '';
    try {
      const joined = await joinRoom(request);
      // The player left the sub-screen while the join was in flight: there is
      // no screen for the room to take over, so it is let go.
      if (screen !== 'start' && screen !== 'join') {
        void joined.leave();
        return;
      }
      room = joined;
      duelRole = request.role;
      duelName = request.name;
      enterTakeover('duel');
    } catch (cause) {
      // A refused join (a wrong protocol version, a full room) arrives here
      // as a thrown error with the server's own message in it, which is
      // exactly what should be shown rather than a generic failure.
      joinError = cause instanceof Error ? cause.message : 'Could not join that room.';
      // The commonest reason a join is refused out of the blue is a tab left
      // open across a deploy: the server speaks a newer protocol than this
      // bundle. Ask for the newer build now; it is applied once the player is
      // back on the tabs.
      checkForUpdate();
    }
  }

  /* --- pages that belong to no one screen ------------------------------- */

  let profileOpen = $state(false);
  /** Settings is reached from the profile sheet, and from the Game menu in a game. */
  let settingsOpen = $state(false);
  let howToPlayOpen = $state(false);
  let myScoresOpen = $state(false);
  let accountOpen = $state(false);
  let passwordOpen = $state(false);
  let pickerOpen = $state(false);
  let installStepsOpen = $state(false);

  function openProfile(): void {
    profileOpen = true;
  }

  function openSettings(): void {
    settingsOpen = true;
  }

  function openHowToPlay(): void {
    howToPlayOpen = true;
  }

  /**
   * The account form: from the profile sheet, the You tab, the Daily tab and
   * the results screen's guest prompt. It is a page laid over whatever asked
   * for it, so signing in from a result leaves the result where it was.
   */
  function openAccount(): void {
    accountOpen = true;
  }

  /** The profile sheet's install row: the browser's prompt, or the written steps where there is none. */
  function installFromProfile(): void {
    if (install.availability === 'installable') void promptInstall();
    else if (install.availability === 'ios-manual') installStepsOpen = true;
  }

  /* --- the frame -------------------------------------------------------- */

  // The shell's `AppFrame` decides nothing: the glyphs are snippets declared
  // in the template below, the ids are this file's `TabId`. Daily carries the
  // "something is waiting" dot until today's daily has been played. The
  // play-together tab is "Together" in every one of the owner's games: it
  // holds Versus and Co-op (`docs/decisions/0054`).
  const tabs: ShellTab[] = $derived([
    { id: 'home', label: 'Home', icon: homeIcon, testid: 'tab-home' },
    {
      id: 'daily',
      label: 'Daily',
      icon: dailyIcon,
      dot: dailyIsWaiting(daily.info, dailyPlayedDate),
      dotLabel: "today's daily not played yet",
      testid: 'tab-daily',
    },
    { id: 'together', label: 'Together', icon: togetherIcon, testid: 'tab-together' },
    { id: 'you', label: 'You', icon: youIcon, testid: 'tab-you' },
  ]);

  function selectTab(id: string): void {
    if (id === 'home' || id === 'daily' || id === 'together' || id === 'you') activeTab = id;
  }

  const player = $derived(account.status === 'signed-in' ? account.player : null);
  const avatar = $derived({
    initial: player === null ? null : player.username.charAt(0).toUpperCase(),
    label: 'Your profile and settings',
    name: player?.username ?? 'Guest',
  });
</script>

{#snippet homeIcon()}<Icon name="home" size={24} />{/snippet}
{#snippet dailyIcon()}<Icon name="calendar" size={24} />{/snippet}
{#snippet togetherIcon()}<Icon name="together" size={24} />{/snippet}
{#snippet youIcon()}<Icon name="person" size={24} />{/snippet}

<!-- Interlock's mark: its own glyph, two interlocking blocks, in the frame's
     accent tile (`AppFrame`'s `game`, shell 1.4.0). The frame draws it on
     Home beside the name, as the page's heading; beside every other tab's
     title; and atop the sidebar from 1024 px. -->
{#snippet mark()}<InterlockMark />{/snippet}

<!-- `data-commit` is how the deploy's health gate proves the served bundle is
     the pushed commit; Settings' build line keeps the same constant in the
     bundle (`nav/Settings.svelte`). -->
<div class="app" class:app--solo={onBoard} data-commit={__COMMIT_SHA__}>
  {#if screen === 'tabs'}
    <AppFrame
      {tabs}
      active={activeTab}
      onselect={selectTab}
      home="home"
      game={{ name: INTERLOCK_SIGNATURE.name, mark }}
      {avatar}
      onavatar={openProfile}
      {tabHistory}
      navLabel="Main"
      testids={{
        tabs: 'main-tabs',
        avatar: 'account-chip',
        title: 'tab-title',
        mark: 'brand-mark',
      }}
    >
      {#if activeTab === 'home'}
        <HomeTab
          onNewGame={() => {
            pickerOpen = true;
          }}
          onDaily={startDaily}
          onDailyBoard={() => {
            activeTab = 'daily';
          }}
          onMyScores={() => {
            myScoresOpen = true;
          }}
        />
      {:else if activeTab === 'daily'}
        <DailyTab onDaily={startDaily} onSignIn={openAccount} />
      {:else if activeTab === 'together'}
        <TogetherTab
          onStart={openStart}
          onJoin={openJoin}
          onLocal={() => {
            localSetupOpen = true;
          }}
        />
      {:else}
        <YouTab
          onProfile={openProfile}
          onMyScores={() => {
            myScoresOpen = true;
          }}
          onSignIn={openAccount}
        />
      {/if}
    </AppFrame>
  {:else if screen === 'game' && modeConfig}
    {#key encodeModeConfig(modeConfig)}
      <GameScreen
        config={modeConfig}
        {dailySeed}
        onFinish={finishRun}
        onQuit={toHomeTab}
        onSettings={openSettings}
        onHowToPlay={openHowToPlay}
        paused={settingsOpen || howToPlayOpen}
      />
    {/key}
  {:else if screen === 'results' && outcome && modeConfig}
    <ResultsScreen
      {outcome}
      config={modeConfig}
      modeLabel={buildMode(modeConfig).copy.name}
      best={bestResult}
      score={runScore}
      daily={dailySeed !== null}
      onRematch={rematch}
      onMenu={toHomeTab}
      onSignIn={openAccount}
    />
  {:else if screen === 'start'}
    {#key startPreset}
      <StartScreen
        preset={startPreset}
        onBack={toTogetherTab}
        onJoin={enterRoom}
        error={joinError}
      />
    {/key}
  {:else if screen === 'join'}
    <JoinScreen
      onBack={toTogetherTab}
      onJoin={enterRoom}
      error={joinError}
      prefillCode={pendingJoinCode}
      onInviteSpent={() => {
        pendingJoinCode = '';
      }}
    />
  {:else if screen === 'local' && localRound}
    {#key localRoundKey}
      <LocalRoundScreen
        players={localRound.players}
        config={localRound.config}
        onSetup={backToLocalSetup}
        onHome={toHomeTab}
        onSettings={openSettings}
        onHowToPlay={openHowToPlay}
        onplaying={(playing) => {
          localPlaying = playing;
        }}
        paused={settingsOpen || howToPlayOpen}
      />
    {/key}
  {:else if screen === 'admin'}
    <AdminScreen onBack={leaveTakeover} />
  {:else if screen === 'duel' && room}
    {#key room}
      <DuelScreen
        {room}
        role={duelRole}
        name={duelName}
        onLeave={toTogetherTab}
        onSettings={openSettings}
        onHowToPlay={openHowToPlay}
        overlayOpen={settingsOpen || howToPlayOpen}
      />
    {/key}
  {/if}
</div>

<AccountMenu
  open={profileOpen}
  onclose={() => {
    profileOpen = false;
  }}
  onMyScores={() => {
    myScoresOpen = true;
  }}
  onHowToPlay={openHowToPlay}
  onSettings={openSettings}
  onInstall={installFromProfile}
  onAdmin={() => {
    enterTakeover('admin');
  }}
  onSignIn={openAccount}
  onChangePassword={() => {
    passwordOpen = true;
  }}
/>
<NewGamePicker
  open={pickerOpen}
  onclose={() => {
    pickerOpen = false;
  }}
  onStart={startMode}
/>
<LocalSetupPicker
  open={localSetupOpen}
  onclose={() => {
    localSetupOpen = false;
  }}
  onStart={startLocalRound}
/>
<Settings
  open={settingsOpen}
  onclose={() => {
    settingsOpen = false;
  }}
/>
<HowToPlayPage
  open={howToPlayOpen}
  onclose={() => {
    howToPlayOpen = false;
  }}
/>
<MyScoresPage
  open={myScoresOpen}
  onclose={() => {
    myScoresOpen = false;
  }}
  onPlay={() => {
    myScoresOpen = false;
    activeTab = 'home';
    pickerOpen = true;
  }}
/>
<AccountPage
  open={accountOpen}
  onclose={() => {
    accountOpen = false;
  }}
/>
<ChangePasswordPage
  open={passwordOpen}
  onclose={() => {
    passwordOpen = false;
  }}
/>
<InstallSheet
  open={installStepsOpen}
  title={IOS_INSTALL_SHEET_TITLE}
  intro={IOS_INSTALL_INTRO}
  steps={IOS_INSTALL_STEPS}
  testid="profile-install-steps"
  onclose={() => {
    installStepsOpen = false;
  }}
/>
<ConfettiBurst trigger={celebration.trigger} />

<style>
  /*
   * The app's column (`packages/shell/INTEGRATION.md`, "The look"): a
   * phone's width (`--ui-column`) and centred; from one pixel wider it sits on
   * the backdrop with the column's edge, and from 1024 px the column widens
   * (`--ui-column-wide`) for the sidebar and the templates' two-column
   * layouts. The shell's frames (`AppFrame`, `GameFrame`) are the height of
   * the screen and fill it.
   */
  .app {
    position: relative;
    flex: 1 1 auto;
    width: 100%;
    max-width: var(--ui-column);
    min-height: 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    background: var(--ui-ground);
  }

  @media (min-width: 30.0625rem) {
    .app {
      box-shadow: var(--ui-shadow-column);
    }
  }

  @media (min-width: 1024px) {
    .app {
      max-width: var(--ui-column-wide);
    }
  }

  /*
   * A phone on its side, during a solo run: the whole window, so the shell's
   * `GameFrame` (`landscapeSplit`, shell 1.5.0) has room for the board on the
   * left and the bar and the pads in a panel beside it, instead of a short
   * board between the bar and the pads in a phone-wide column
   * (`packages/shell/INTEGRATION.md`, `GameFrame`; `docs/decisions/0057`).
   * The same query as the frame's own; the menus keep the column.
   */
  @media (max-width: 1023.98px) and (max-height: 30rem) and (orientation: landscape) {
    .app--solo {
      max-width: none;
      box-shadow: none;
    }
  }
</style>
